import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Favorite, FavoriteBorder, Add, Remove, Bolt, LocalFireDepartment } from "@mui/icons-material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp"; // أو أي أيقونة مناسبة
import { BranchProduct } from "../../../types";
import { useProductContext } from "../../../context/ProductContext";
import RatingStars from "./RatingStars";
interface ListProductCardProps {
  product: BranchProduct;
  getCardColors: string;
  t: (key: string) => string;
}

const ListProductCard: React.FC<ListProductCardProps> = ({ product, getCardColors, t }) => {
  const navigate = useNavigate();
  const { addToCart } = useProductContext();
  const [isHovered, setIsHovered] = useState(false);
  const [isWishlist, setIsWishlist] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleAddToCart = (qty: number) => {
    for (let i = 0; i < qty; i++) addToCart(product.product.id);
  };

  const handleWhatsAppPurchase = (qty: number) => {
    // منطق إرسال الطلب عبر واتساب
  };

  const toggleWishlist = () => setIsWishlist(!isWishlist);

  const images = product.product.images || [];

const stockStatus = {
  color: !product.is_available
    ? "bg-red-100 text-red-700"
    : product.stock <= 3
    ? "bg-yellow-100 text-yellow-700"
    : "bg-green-100 text-green-700",
  text: !product.is_available
    ? t("productCard.status.outOfStock") || "غير متوفر"
    : product.stock <= 3
    ? t("productCard.status.lowStock")?.replace("{{count}}", `${product?.stock}`) || `الكمية المتبقية: ${product.stock}`
    : t("productCard.status.inStock") || "متوفر",
};


  const rating = product.product.average_rating || 0;
  const badge = product.product.badge;
  const isFeatured = product.product.is_featured;

  const maxQuantity = product.stock || 10;

  return (
    <div
      data-product-id={product.id}
      className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col sm:flex-row"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image */}
      <div
        className="relative w-full sm:w-1/3 aspect-square sm:aspect-auto cursor-pointer"
        onClick={() => navigate(`/product/${product.product.id}`)}
      >
        <img
          src={images[currentImageIndex]?.image || "/api/placeholder/400/400"}
          alt={product.product.name}
          className="w-full h-full object-cover"
        />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {badge && (
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${getCardColors}`}>
              {badge}
            </span>
          )}
          {isFeatured && (
            <span className="text-xs font-bold px-2 py-1 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
              Featured
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 sm:p-6">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">{product.product.name}</h3>
            {rating > 0 && <RatingStars rating={rating} />}
          </div>

          <button onClick={toggleWishlist} className="bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors">
            {isWishlist ? <Favorite className="w-5 h-5 text-red-500" /> : <FavoriteBorder className="w-5 h-5 text-gray-600" />}
          </button>
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.product.description}</p>

        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-2xl font-bold text-primary">ج.م {product.price}</span>
            {product.product.base_price > product.price && (
              <span className="text-sm text-gray-400 line-through ml-2">ج.م {product.product.base_price}</span>
            )}
          </div>
          <span className={`text-sm px-3 py-1 rounded-full ${stockStatus.color}`}>{stockStatus.text}</span>
        </div>

        <div className="flex items-center gap-3">
          {/* Quantity */}
          <div className="flex items-center border rounded-lg">
            <button
              className="px-3 py-2 hover:bg-gray-50 disabled:opacity-40"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
            >
              <Remove className="w-4 h-4" />
            </button>
            <span className="px-3 py-2 min-w-[40px] text-center font-medium">{quantity}</span>
            <button
              className="px-3 py-2 hover:bg-gray-50 disabled:opacity-40"
              onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
              disabled={quantity >= maxQuantity}
            >
              <Add className="w-4 h-4" />
            </button>
          </div>

          {/* Actions */}
          <div className="flex-1 flex gap-2">
            <button
              className={`flex-1 py-3 bg-gradient-to-r ${getCardColors} text-white font-medium rounded-lg hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-2`}
              onClick={() => handleAddToCart(quantity)}
              disabled={!product.is_available}
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden sm:inline">{t("productCard.addToCart")}</span>
              <span className="sm:hidden">أضف</span>
            </button>

            {product.is_available && (
              <button
                className="px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium rounded-lg hover:opacity-90 transition-all duration-300 flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleWhatsAppPurchase(quantity);
                }}
              >
                <WhatsAppIcon />
              </button>
            )}
          </div>
        </div>

        {/* Quick info */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Bolt className="w-4 h-4" />
            <span>{t("productCard.fastDelivery") || "توصيل سريع"}</span>
          </div>
          <div className="flex items-center gap-1">
            <LocalFireDepartment className="w-4 h-4" />
            <span>{product.product.total_sold || 0} {t("productCard.sold") || "مباع"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListProductCard;
